
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../dashboard/components/DashboardLayout';
import ProductList from './components/ProductList';
import ProductForm from './components/ProductForm';
import ProductDetail from './components/ProductDetail';
import ImportHistory from './components/ImportHistory';
import ImportForm from './components/ImportForm';
import Cart, { CartItem } from './components/Cart';

export interface ProductVariant {
  size: string;
  color: string;
  quantity: number;
  images?: string[];
}

export interface Product {
  id: string;
  name: string;
  sizes: string[];
  colors: string[];
  variants: ProductVariant[];
  productionPrice: number;
  sellingPrice: number;
  image: string;
  images?: string[];
  description?: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImportRecord {
  id: string;
  productId: string;
  productName: string;
  size: string;
  color: string;
  quantity: number;
  importDate: string;
  note?: string;
}

export default function ProductsPage() {
  const { userProfile } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showProductForm, setShowProductForm] = useState(false);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Cart state
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);

  const categories = ['Quần Áo', 'Vợt Thi Đấu', 'Phụ kiện thi đấu', 'Phụ kiện khác'];

  // Check if user is partner
  const isPartner = userProfile?.role === 'partner';

  // Load products from Supabase
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (productsError) {
        console.error('Error loading products:', productsError);
        setError('Không thể tải danh sách sản phẩm');
        return;
      }

      // Load product variants
      const { data: variantsData, error: variantsError } = await supabase
        .from('product_variants')
        .select('*');

      if (variantsError) {
        console.error('Error loading variants:', variantsError);
      }

      // Transform data to match local format
      const transformedProducts: Product[] = (productsData || []).map(product => {
        const productVariants = (variantsData || []).filter(v => v.product_id === product.id);
        
        // Extract unique sizes and colors
        const sizes = [...new Set(productVariants.map(v => v.size))];
        const colors = [...new Set(productVariants.map(v => v.color))];
        
        // Transform variants
        const variants: ProductVariant[] = productVariants.map(v => ({
          size: v.size,
          color: v.color,
          quantity: v.stock_quantity,
          images: v.images ? JSON.parse(v.images) : [v.image_url || product.image_url]
        }));

        return {
          id: product.id,
          name: product.name,
          sizes,
          colors,
          variants,
          productionPrice: product.production_price || (product.base_price * 0.6), // Use actual production price or fallback
          sellingPrice: product.base_price,
          image: product.image_url || '',
          images: product.images ? JSON.parse(product.images) : [product.image_url || ''],
          description: product.description || '',
          category: product.category || '',
          createdAt: product.created_at.split('T')[0],
          updatedAt: product.updated_at.split('T')[0]
        };
      });

      setProducts(transformedProducts);
    } catch (err) {
      console.error('Error in loadProducts:', err);
      setError('Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Product management functions
  const handleAddProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setLoading(true);

      // Insert product into Supabase
      const { data: newProduct, error: productError } = await supabase
        .from('products')
        .insert({
          name: productData.name,
          description: productData.description,
          base_price: productData.sellingPrice,
          production_price: productData.productionPrice, // Add production price field
          category: productData.category,
          brand: 'Navik Sport',
          image_url: productData.image,
          images: JSON.stringify(productData.images || [productData.image]),
          is_active: true
        })
        .select()
        .single();

      if (productError) {
        console.error('Error adding product:', productError);
        alert('Có lỗi khi thêm sản phẩm: ' + productError.message);
        return;
      }

      // Insert variants
      const variantInserts = productData.variants.map(variant => ({
        product_id: newProduct.id,
        size: variant.size,
        color: variant.color,
        stock_quantity: variant.quantity,
        image_url: variant.images?.[0] || productData.image,
        images: JSON.stringify(variant.images || [])
      }));

      const { error: variantsError } = await supabase
        .from('product_variants')
        .insert(variantInserts);

      if (variantsError) {
        console.error('Error adding variants:', variantsError);
        alert('Có lỗi khi thêm biến thể sản phẩm: ' + variantsError.message);
        return;
      }

      // Reload products
      await loadProducts();
      alert('Thêm sản phẩm thành công!');
    } catch (err) {
      console.error('Error in handleAddProduct:', err);
      alert('Có lỗi xảy ra khi thêm sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!editingProduct) return;

    try {
      setLoading(true);

      // Update product in Supabase
      const { error: productError } = await supabase
        .from('products')
        .update({
          name: productData.name,
          description: productData.description,
          base_price: productData.sellingPrice,
          production_price: productData.productionPrice, // Update production price field
          category: productData.category,
          image_url: productData.image,
          images: JSON.stringify(productData.images || [productData.image]),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingProduct.id);

      if (productError) {
        console.error('Error updating product:', productError);
        alert('Có lỗi khi cập nhật sản phẩm: ' + productError.message);
        return;
      }

      // Delete existing variants
      await supabase
        .from('product_variants')
        .delete()
        .eq('product_id', editingProduct.id);

      // Insert new variants
      const variantInserts = productData.variants.map(variant => ({
        product_id: editingProduct.id,
        size: variant.size,
        color: variant.color,
        stock_quantity: variant.quantity,
        image_url: variant.images?.[0] || productData.image,
        images: JSON.stringify(variant.images || [])
      }));

      const { error: variantsError } = await supabase
        .from('product_variants')
        .insert(variantInserts);

      if (variantsError) {
        console.error('Error updating variants:', variantsError);
        alert('Có lỗi khi cập nhật biến thể sản phẩm: ' + variantsError.message);
        return;
      }

      // Reload products
      await loadProducts();
      alert('Cập nhật sản phẩm thành công!');
    } catch (err) {
      console.error('Error in handleEditProduct:', err);
      alert('Có lỗi xảy ra khi cập nhật sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) return;

    try {
      setLoading(true);

      // Delete variants first
      const { error: variantsError } = await supabase
        .from('product_variants')
        .delete()
        .eq('product_id', productId);

      if (variantsError) {
        console.error('Error deleting variants:', variantsError);
      }

      // Soft delete product (set is_active to false)
      const { error: productError } = await supabase
        .from('products')
        .update({ is_active: false })
        .eq('id', productId);

      if (productError) {
        console.error('Error deleting product:', productError);
        alert('Có lỗi khi xóa sản phẩm: ' + productError.message);
        return;
      }

      // Reload products
      await loadProducts();
      alert('Xóa sản phẩm thành công!');
    } catch (err) {
      console.error('Error in handleDeleteProduct:', err);
      alert('Có lỗi xảy ra khi xóa sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVariantQuantity = async (productId: string, size: string, color: string, newQuantity: number) => {
    try {
      // Update in Supabase
      const { error } = await supabase
        .from('product_variants')
        .update({ stock_quantity: newQuantity })
        .eq('product_id', productId)
        .eq('size', size)
        .eq('color', color);

      if (error) {
        console.error('Error updating variant quantity:', error);
        alert('Có lỗi khi cập nhật số lượng: ' + error.message);
        return;
      }

      // Update local state
      setProducts(
        products.map(p => {
          if (p.id === productId) {
            const updatedVariants = p.variants.map(v =>
              v.size === size && v.color === color ? { ...v, quantity: newQuantity } : v
            );
            return { ...p, variants: updatedVariants, updatedAt: new Date().toISOString().split('T')[0] };
          }
          return p;
        })
      );

      // Update viewing product if it's the same product
      if (viewingProduct && viewingProduct.id === productId) {
        const updatedVariants = viewingProduct.variants.map(v =>
          v.size === size && v.color === color ? { ...v, quantity: newQuantity } : v
        );
        setViewingProduct({ ...viewingProduct, variants: updatedVariants });
      }
    } catch (err) {
      console.error('Error in handleUpdateVariantQuantity:', err);
      alert('Có lỗi xảy ra khi cập nhật số lượng');
    }
  };

  // View product function
  const handleViewProduct = (product: Product) => {
    setViewingProduct(product);
    setShowProductDetail(true);
  };

  // Edit product function
  const handleEditProductClick = (product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  // Save product function
  const handleSaveProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingProduct) {
      await handleEditProduct(productData);
    } else {
      await handleAddProduct(productData);
    }
    setShowProductForm(false);
    setEditingProduct(null);
  };

  // Cart functions
  const handleAddToCart = (item: Omit<CartItem, 'id'>) => {
    const existingItemIndex = cartItems.findIndex(
      cartItem => 
        cartItem.productId === item.productId && 
        cartItem.color === item.color && 
        cartItem.size === item.size
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...cartItems];
      const newQuantity = updatedItems[existingItemIndex].quantity + item.quantity;
      
      if (newQuantity <= item.maxStock) {
        updatedItems[existingItemIndex].quantity = newQuantity;
        setCartItems(updatedItems);
      } else {
        alert(`Không thể thêm. Số lượng tối đa cho sản phẩm này là ${item.maxStock}`);
      }
    } else {
      const newItem: CartItem = {
        ...item,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
      };
      setCartItems([...cartItems, newItem]);
    }
  };

  const handleUpdateCartQuantity = (itemId: string, quantity: number) => {
    setCartItems(cartItems.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    ));
  };

  const handleRemoveCartItem = (itemId: string) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  const handleClearCart = () => {
    if (confirm('Bạn có chắc chắn muốn xóa tất cả sản phẩm trong giỏ hàng?')) {
      setCartItems([]);
    }
  };

  const handleCompleteOrder = async (orderData: {
    items: CartItem[];
    customerName: string;
    customerPhone: string;
    customerAddress: string;
    notes: string;
    totalAmount: number;
  }) => {
    try {
      if (!userProfile) {
        alert('Vui lòng đăng nhập để tạo đơn hàng!');
        return;
      }

      // Generate order number
      const orderNumber = `DH${Date.now()}`;

      // Prepare order items for database
      const orderItems = orderData.items.map(item => ({
        product_id: item.productId,
        product_name: item.productName,
        color: item.color,
        size: item.size,
        quantity: item.quantity,
        price: item.price,
        image_url: item.image
      }));

      // Create order in database
      const { data: orderResult, error: orderError } = await supabase
        .from('orders')
        .insert({
          order_number: orderNumber,
          created_by: userProfile.authUserId,
          created_by_name: userProfile.fullName || userProfile.email,
          created_by_role: userProfile.role,
          customer_name: orderData.customerName,
          customer_phone: orderData.customerPhone,
          customer_address: orderData.customerAddress,
          notes: orderData.notes,
          total_amount: orderData.totalAmount,
          status: 'pending'
        })
        .select()
        .single();

      if (orderError) {
        console.error('Error creating order:', orderError);
        alert('Lỗi khi tạo đơn hàng. Vui lòng thử lại!');
        return;
      }

      // Create order items
      const orderItemsWithOrderId = orderItems.map(item => ({
        ...item,
        order_id: orderResult.id
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItemsWithOrderId);

      if (itemsError) {
        console.error('Error creating order items:', itemsError);
        alert('Lỗi khi tạo chi tiết đơn hàng. Vui lòng thử lại!');
        return;
      }

      // Update product stock
      for (const item of orderData.items) {
        const product = products.find(p => p.id === item.productId);
        if (product) {
          const variant = product.variants.find(v => 
            v.color === item.color && v.size === item.size
          );
          if (variant) {
            const newStock = variant.quantity - item.quantity;
            await supabase
              .from('product_variants')
              .update({ stock_quantity: Math.max(0, newStock) })
              .eq('product_id', item.productId)
              .eq('size', item.size)
              .eq('color', item.color);
          }
        }
      }

      alert(`Đơn hàng ${orderNumber} đã được tạo thành công!`);
      
      // Clear cart and close modals
      setCartItems([]);
      setShowCart(false);
      
      // Reload products to update stock
      loadProducts();

    } catch (error) {
      console.error('Error in handleCompleteOrder:', error);
      alert('Có lỗi xảy ra khi tạo đơn hàng. Vui lòng thử lại!');
    }
  };

  const getTotalQuantity = (product: Product): number => {
    return product.variants.reduce((total, variant) => total + variant.quantity, 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Đang tải dữ liệu...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-600 text-2xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadProducts}
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg"
          >
            Thử lại
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header with Cart Button */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Quản lý sản phẩm</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                {isPartner ? 'Xem danh sách sản phẩm và thêm vào giỏ hàng' : 'Quản lý kho hàng và sản phẩm'}
              </p>
            </div>
            
            {/* Cart Button */}
            <button
              onClick={() => setShowCart(true)}
              className="relative bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <i className="ri-shopping-cart-line"></i>
              <span className="hidden sm:inline">Giỏ hàng</span>
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItems.length > 99 ? '99+' : cartItems.length}
                </span>
              )}
            </button>
          </div>

          {/* Action Buttons for Manager */}
          {!isPartner && (
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={() => setShowProductForm(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                <span className="hidden sm:inline">Thêm sản phẩm</span>
              </button>
              <button
                onClick={() => setShowImportForm(true)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-upload-line"></i>
                <span className="hidden sm:inline">Nhập hàng</span>
              </button>
              <button
                onClick={() => setShowImportHistory(true)}
                className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-history-line"></i>
                <span className="hidden sm:inline">Lịch sử nhập</span>
              </button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Tìm kiếm sản phẩm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white pr-8"
            >
              <option value="">Tất cả danh mục</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Products List */}
        <ProductList
          products={filteredProducts}
          onAddProduct={() => setShowProductForm(true)}
          onEditProduct={handleEditProductClick}
          onViewProduct={handleViewProduct}
          onDeleteProduct={handleDeleteProduct}
          onUpdateVariantQuantity={handleUpdateVariantQuantity}
          onImportStock={() => setShowImportForm(true)}
          onViewHistory={() => setShowImportHistory(true)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterCategory={filterCategory}
          onFilterChange={setFilterCategory}
          categories={categories}
          getTotalQuantity={getTotalQuantity}
          onAddToCart={handleAddToCart}
        />

        {/* Cart Sidebar */}
        {showCart && (
          <Cart
            items={cartItems}
            onUpdateQuantity={handleUpdateCartQuantity}
            onRemoveItem={handleRemoveCartItem}
            onClearCart={handleClearCart}
            onClose={() => setShowCart(false)}
            onCompleteOrder={handleCompleteOrder}
          />
        )}

        {/* Product Form Modal */}
        {showProductForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <ProductForm
                product={editingProduct}
                onSubmit={handleSaveProduct}
                onCancel={() => {
                  setShowProductForm(false);
                  setEditingProduct(null);
                }}
                categories={categories}
              />
            </div>
          </div>
        )}

        {/* Product Detail Modal */}
        {showProductDetail && viewingProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="max-h-[90vh] overflow-y-auto">
                <ProductDetail
                  product={viewingProduct}
                  onEdit={() => {
                    setEditingProduct(viewingProduct);
                    setShowProductDetail(false);
                    setViewingProduct(null);
                    setShowProductForm(true);
                  }}
                  onBack={() => {
                    setShowProductDetail(false);
                    setViewingProduct(null);
                  }}
                  onUpdateVariantQuantity={handleUpdateVariantQuantity}
                />
              </div>
            </div>
          </div>
        )}

        {/* Import Form Modal */}
        {showImportForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <ImportForm
                products={products}
                onSubmit={(importData) => {
                  // Handle import logic here
                  setShowImportForm(false);
                }}
                onCancel={() => setShowImportForm(false)}
              />
            </div>
          </div>
        )}

        {/* Import History Modal */}
        {showImportHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <ImportHistory
                importHistory={[]}
                onBack={() => setShowImportHistory(false)}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
